import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { Observable, shareReplay, startWith } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { loginRoutePaths } from '@ebanking/features/login/login.route-paths';
import { SvgIconComponent } from '~/shared/form-elements/components/svg-icon/svg-icon.component';
import { TranslatePipe } from '@ngx-translate/core';
import { AssetByColorModePipe } from '~/shared/pipes/asset-by-color-mode.pipe';
import { IonicModule } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { cancelAuthentication } from '@qvs/core/auth';
import { cancelRegistration } from '@qvs/core/registration';
import { ClickKeydownEnterSpaceDirective } from '~/shared/directives/clickKeyDownEnterSpace.directive';
import { VersionInfoComponent } from '@qvs/shared';

@Component({
  selector: 'qvs-login-page',
  standalone: true,
  imports: [
    CommonModule,
    RouterOutlet,
    RouterLink,
    RouterLinkActive,
    SvgIconComponent,
    TranslatePipe,
    AssetByColorModePipe,
    IonicModule,
    ClickKeydownEnterSpaceDirective,
    VersionInfoComponent
  ],
  templateUrl: './login.page.component.html',
  styleUrls: ['./login.page.component.scss']
})
export class LoginPageComponent {
  loginRoutePaths = loginRoutePaths;
  readonly logo = { light: 'assets/Logo_EFG.svg', dark: 'assets/Logo_EFG-dark.svg' };

  isNotLoginRoute$: Observable<boolean> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null), // emit once on init
    map(() => this.router.url),
    map((url) => {
      const clean = url.split('?')[0].split('#')[0];
      return !clean.endsWith(loginRoutePaths.LOGIN);
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  isLargerPaddingNeeded$: Observable<boolean> = this.router.events.pipe(
    filter((event): event is NavigationEnd => event instanceof NavigationEnd),
    startWith(null), // emit once on init
    map(() => this.router.url),
    map((url) => {
      const clean = url.split('?')[0].split('#')[0];
      return (
        clean.endsWith(loginRoutePaths.MOBILE) ||
        clean.endsWith(loginRoutePaths.LOGIN) ||
        clean.endsWith(loginRoutePaths.REGISTRATION)
      );
    }),
    distinctUntilChanged(),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  backgroundImages: { src: string; class: string }[] = [];

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly store: Store
  ) {}

  cancelPolling() {
    let activeRoute: ActivatedRoute = this.route;

    while (activeRoute.firstChild) {
      activeRoute = activeRoute.firstChild;
    }

    if (activeRoute.snapshot.routeConfig?.path === loginRoutePaths.SCAN) {
      this.store.dispatch(cancelAuthentication());
    }

    if (activeRoute.snapshot.routeConfig?.path === loginRoutePaths.REGISTRATION_QR) {
      this.store.dispatch(cancelRegistration());
    }

    this.router.navigate([loginRoutePaths.LOGIN]);
  }

  updateBackgroundImages() {
    const routeWithData = this.findRouteWithBackgroundImages(this.route);
    routeWithData?.data.subscribe((data) => {
      this.backgroundImages = data.backgroundImages ?? [];
    });
  }

  private findRouteWithBackgroundImages(route: ActivatedRoute): ActivatedRoute | null {
    // Check current route first
    if (route.snapshot.data.backgroundImages) {
      return route;
    }

    // Recursively check child routes
    if (route.firstChild) {
      return this.findRouteWithBackgroundImages(route.firstChild);
    }

    return null;
  }
}
